import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  GetContextMenuItems,
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowDragEndEvent,
} from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { ChuteSchedulerActions } from '@/app/slices/chuteScheduler';
import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, sideBar } from '@/grid/defaults';
import { AspirationResultRow, ChuteScheduleRow } from '@/rtk-query.gql';

import { ColumnDef, excelStyles } from '../../utils/columnDefs';
import {
  groupBySite,
  isSameSite,
  numberPerSite,
} from '../../utils/scheduleMath';

const storageKeyFor = (gridKey: number) => `chute-${gridKey}-scheduler-grid`;

const hhmmToMinutes = (v?: string | null) =>
  v ? Math.floor(+v / 100) * 60 + (+v % 100) : 0;

const minutesToHHMM = (m: number) =>
  `${Math.floor(m / 60) % 24}${(m % 60).toString().padStart(2, '0')}`.padStart(
    4,
    '0',
  );

const resequenceTimes = (
  ordered: ChuteScheduleRow[],
  interval: number,
  chute: number,
) => {
  const times = new Map<unknown, string>();
  groupBySite(ordered).forEach((group) => {
    const start = hhmmToMinutes(group[0].aspTime) || 0;
    group.forEach((r, i) => {
      const block = chute > 1 ? Math.floor(i / chute) : i;
      times.set(r.aspirationId, minutesToHHMM(start + block * interval));
    });
  });
  return ordered.map((r) => ({
    ...r,
    aspTime: times.get(r.aspirationId) ?? r.aspTime,
  }));
};

export interface ChuteSchedulerGridProps {
  className?: string;
  columnDefs: ColumnDef[];
  gridKey: number;
  rowData: ChuteScheduleRow[] | AspirationResultRow[];
  refreshData: () => void;
  setGridApi?: (api: GridApi) => void;
  clearFabClass?: string;
  saveFabClass?: string;
  gridRef: React.RefObject<AgGridReact>;
  date?: string | null;
  getContextMenuItems: GetContextMenuItems<ChuteScheduleRow>;
}

export const Grid = ({
  className,
  columnDefs,
  gridKey,
  rowData,
  refreshData,
  setGridApi,
  clearFabClass = '-top-0 right-0',
  saveFabClass = '-top-0 right-14',
  gridRef,
  date,
  getContextMenuItems,
}: ChuteSchedulerGridProps) => {
  const defaultGridOptions = useDefaultGridOptions();
  const dispatch = useDispatch();
  const { mode } = useAppSelector((s) => s.preferences);
  const { interval, multiChuteCount } = useAppSelector((s) => s.chuteScheduler);
  const [params] = useSearchParams();
  const siteId = params.get('site');

  const skipResetRef = useRef(false);
  const isSchedulingTab = gridKey === 0;

  const storageKey = storageKeyFor(gridKey);

  const [draggedNode, setDraggedNode] = useState<any>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const getAllRows = useCallback(() => {
    const list: ChuteScheduleRow[] = [];
    gridRef.current!.api.forEachNodeAfterFilterAndSort((n) => {
      list.push(n.data as ChuteScheduleRow);
    });
    return list;
  }, [gridRef]);

  const updateSelected = useCallback(() => {
    const api = gridRef.current!.api;
    if (!api) return;

    const selectedNodes = api.getSelectedNodes();
    if (selectedNodes.length) {
      dispatch({
        type: isSchedulingTab
          ? ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS
          : ChuteSchedulerActions.UPDATE_SELECTED_RESULTS_ROWS,
        rows: selectedNodes.map((n) => n.data as ChuteScheduleRow),
      });
    }
  }, [dispatch, gridRef, isSchedulingTab]);

  const onModelUpdated = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    api.refreshCells({ force: true });
    api.redrawRows();
  }, [gridRef]);

  const renumberOnly = useCallback(() => {
    skipResetRef.current = true;
    const api = gridRef.current!.api;
    const nodes: IRowNode[] = [];
    api.forEachNode((node) => nodes.push(node));
    numberPerSite(nodes.map((node) => node.data)).forEach((row, index) =>
      nodes[index].setData(row),
    );
    updateSelected();
  }, [gridRef, updateSelected]);

  const saveRowOrder = () => {
    const visible = getAllRows();
    const invisible = (rowData as ChuteScheduleRow[]).filter(
      (r) => !visible.some((v) => v.aspirationId === r.aspirationId),
    );
    const combined = [...visible, ...invisible];
    const withTimes = isSchedulingTab
      ? resequenceTimes(combined, interval, multiChuteCount)
      : combined;
    const numbered = numberPerSite(withTimes);
    const cleaned = numbered.map((row) => {
      const out = { ...row } as Record<string, unknown>;
      return out as ChuteScheduleRow;
    });
    dispatch({
      type: isSchedulingTab
        ? ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS
        : ChuteSchedulerActions.UPDATE_RESULTS_ROWS,
      rows: cleaned,
    });

    updateSelected();
  };

  const onRowDragEnter = useCallback((e: any) => {
    setDraggedNode(e.node);
  }, []);

  const onRowDragMove = useCallback((e: any) => {
    if (e.overNode) {
      setOverIndex(e.overNode.rowIndex);
    }
  }, []);

  const onRowDragLeave = useCallback(() => {
    setOverIndex(null);
  }, []);

  const onRowDragEnd = useCallback(
    (e: RowDragEndEvent) => {
      const api = gridRef.current!.api;
      if (!api) return;

      const dragNodes = e.nodes?.length ? e.nodes : api.getSelectedNodes();

      const overRow = e.overNode?.data as ChuteScheduleRow | undefined;
      if (overRow && dragNodes.some((n) => !isSameSite(n.data, overRow))) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'Cannot move between sites',
            detail: 'Donors can only be moved within their own site.',
            life: 4000,
          }),
        );
        setDraggedNode(null);
        setOverIndex(null);
        return;
      }

      if (overIndex == null || !dragNodes.length) return;

      const movingIds = new Set(
        dragNodes.map((n) => (n.data as ChuteScheduleRow).aspirationId),
      );

      const ordered: ChuteScheduleRow[] = [];
      api.forEachNodeAfterFilterAndSort((n) =>
        ordered.push(n.data as ChuteScheduleRow),
      );

      const movingData = ordered.filter((r) => movingIds.has(r.aspirationId));
      const remaining = ordered.filter((r) => !movingIds.has(r.aspirationId));

      const newOrdered = [
        ...remaining.slice(0, overIndex),
        ...movingData,
        ...remaining.slice(overIndex),
      ];

      const withTimes = isSchedulingTab
        ? resequenceTimes(newOrdered, interval, multiChuteCount)
        : newOrdered;

      const resequenced = numberPerSite(withTimes);

      dispatch({
        type: isSchedulingTab
          ? ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS
          : ChuteSchedulerActions.UPDATE_RESULTS_ROWS,
        rows: resequenced,
      });
      setDraggedNode(null);
      setOverIndex(null);
      updateSelected();
    },
    [
      gridRef,
      overIndex,
      isSchedulingTab,
      interval,
      multiChuteCount,
      dispatch,
      updateSelected,
    ],
  );

  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      setGridApi?.(e.api);
    },
    [setGridApi],
  );

  const onFilterChanged = renumberOnly;
  const onSortChanged = renumberOnly;

  useEffect(() => {
    if (skipResetRef.current) {
      skipResetRef.current = false;
      return;
    }
    gridRef.current?.api?.setFilterModel(null);
    gridRef.current?.api?.applyColumnState({
      defaultState: { sort: null },
    });

    if (rowData && rowData.length > 0) {
      dispatch({
        type: isSchedulingTab
          ? ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS
          : ChuteSchedulerActions.UPDATE_SELECTED_RESULTS_ROWS,
        rows: rowData,
      });
    }
  }, [dispatch, gridRef, isSchedulingTab, rowData]);

  return (
    <div className="relative h-full">
      <WithLocalStorage storageKey={storageKey}>
        <AgGridReact
          gridOptions={defaultGridOptions}
          ref={gridRef}
          className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} ${
            className ?? ''
          }`}
          defaultColDef={{
            ...defaultColDef,
            headerClass: 'tog-header-black',
          }}
          rowData={rowData}
          overlayLoadingTemplate="Please Select a Date and a Site"
          columnDefs={columnDefs as AgGridReactProps['columnDefs']}
          rowSelection="multiple"
          rowMultiSelectWithClick={false}
          rowDragMultiRow
          maintainColumnOrder={true}
          onGridReady={onGridReady}
          onRowDragEnd={onRowDragEnd}
          onRowDragMove={onRowDragMove}
          onRowDragLeave={onRowDragLeave}
          onRowDragEnter={onRowDragEnter}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          onSelectionChanged={updateSelected}
          onModelUpdated={onModelUpdated}
          excelStyles={excelStyles}
          sideBar={sideBar}
          context={{
            siteId,
          }}
          getContextMenuItems={getContextMenuItems}
        />
      </WithLocalStorage>
    </div>
  );
};
